import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ExternalLink, Copy, Check, User, Globe, CreditCard, Settings, Bell, Shield, X, LogOut } from "lucide-react";
import { ResetPasswordDialog } from "@/components/auth/ResetPasswordDialog";
import StripeConnectOnboarding from "@/components/posit/StripeConnectOnboarding";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import DashboardLayout from "@/components/layout/DashboardLayout";

type ProfileFormData = {
  firstName: string;
  lastName: string;
  handle?: string;
  email?: string;
  bio?: string;
};

type NotificationSettings = {
  emailNotifications: boolean;
  marketingEmails: boolean;
  productUpdates: boolean;
  newMessages: boolean;
};

const EditProfile = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<string>('');
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [currentHandle, setCurrentHandle] = useState("");
  const [isHandleAvailable, setIsHandleAvailable] = useState<boolean | null>(null);
  const [isCheckingHandle, setIsCheckingHandle] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [isOAuthUser, setIsOAuthUser] = useState(false);
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    marketingEmails: false,
    productUpdates: true,
    newMessages: true,
  });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [isHandleSaving, setIsHandleSaving] = useState(false);
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNewUser = searchParams.get('newUser') === 'true';
  
  const form = useForm<ProfileFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      handle: "",
      email: "",
      bio: "",
    },
  });

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        // Get user data from our users table
        const { data, error } = await supabase
          .from("users")
          .select("first_name, last_name, subscription_type, handle, bio")
          .eq("id", user.id)
          .single();

        if (error) throw error;

        // Check if user is OAuth-based by looking at their identities
        const isOAuth = user.identities?.some(identity => identity.provider === 'google') || false;
        setIsOAuthUser(isOAuth);

        // If user is Google OAuth user and doesn't have names in DB, extract from metadata
        let firstName = data?.first_name || "";
        let lastName = data?.last_name || "";
        
        if (isOAuth && (!firstName || !lastName)) {
          // Extract names from Google OAuth metadata
          const googleIdentity = user.identities?.find(identity => identity.provider === 'google');
          if (googleIdentity && googleIdentity.identity_data) {
            const metadata = googleIdentity.identity_data;
            firstName = firstName || metadata.given_name || metadata.first_name || "";
            lastName = lastName || metadata.family_name || metadata.last_name || "";
            
            // If we found names in metadata, update the database
            if ((firstName && !data?.first_name) || (lastName && !data?.last_name)) {
              const { error: updateError } = await supabase
                .from("users")
                .update({
                  first_name: firstName,
                  last_name: lastName,
                })
                .eq("id", user.id);
              
              if (updateError) {
              } else {
              }
            }
          }
        }

        if (data) {
          // Store the actual subscription type
          setSubscriptionType(data.subscription_type || 'free');
          // Check for premium access (includes both posit_plus and trial users)
          setIsPremium(data.subscription_type === 'posit_plus' || data.subscription_type === 'trial');
          setCurrentHandle(data.handle || "");
          form.reset({
            firstName: firstName,
            lastName: lastName,
            handle: data.handle || "",
            email: user.email || "",
            bio: data.bio || "",
          });
        }

        // Load notification settings (would come from a separate table in real app)
        // For now, using localStorage as a placeholder
        const savedSettings = localStorage.getItem(`notifications_${user.id}`);
        if (savedSettings) {
          setNotificationSettings(JSON.parse(savedSettings));
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Couldn’t load profile",
          description: "Please try again.",
        });
      }
    };

    fetchUserData();
  }, [user, form, toast]);

  // Handle Stripe Connect onboarding completion
  useEffect(() => {
    const stripeOnboarding = searchParams.get('stripe_onboarding');
    if (stripeOnboarding === 'completed') {
      toast({
        title: "Payment setup complete 🎉",
        description: "Your Stripe Connect account is configured. You can now receive payouts.",
        duration: 6000,
      });
      // Clean up URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('stripe_onboarding');
      navigate(`/dashboard/settings?${newSearchParams.toString()}`, { replace: true });
    } else if (stripeOnboarding === 'refresh') {
      toast({
        title: "Finish payment setup",
        description: "Complete Stripe Connect to start receiving payouts.",
        variant: "destructive",
      });
      // Clean up URL  
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('stripe_onboarding');
      navigate(`/dashboard/settings?${newSearchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate, toast]);

  // Handle validation
  const validateHandle = (handle: string) => {
    if (!handle) return false;
    
    // Handle must be 3-30 characters, alphanumeric and dashes only
    const handleRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]$/;
    if (handle.length < 3 || handle.length > 30) return false;
    if (!handleRegex.test(handle)) return false;
    
    // No consecutive dashes
    if (handle.includes('--')) return false;
    
    return true;
  };

  const checkHandleAvailability = async (handle: string) => {
    if (!handle || !validateHandle(handle) || handle === currentHandle) {
      setIsHandleAvailable(null);
      return;
    }

    setIsCheckingHandle(true);
    try {
      const { data, error } = await supabase
        .from("users")
        .select("handle")
        .eq("handle", handle.toLowerCase())
        .maybeSingle();

      if (error) throw error;
      
      setIsHandleAvailable(!data);
    } catch (error) {
      setIsHandleAvailable(null);
    } finally {
      setIsCheckingHandle(false);
    }
  };

  // Debounced handle check
  useEffect(() => {
    const handle = form.watch("handle");
    if (!handle) {
      setIsHandleAvailable(null);
      return;
    }

    const timer = setTimeout(() => {
      checkHandleAvailability(handle);
    }, 500);

    return () => clearTimeout(timer);
  }, [form.watch("handle")]);

  const onSubmit = async (data: ProfileFormData) => {
    if (!user) return;
    setIsLoading(true);

    try {
      const updateData: any = {
        first_name: data.firstName,
        last_name: data.lastName,
        bio: data.bio,
      };

      // Note: Handle is now saved separately with its own save button

      const { error } = await supabase
        .from("users")
        .update(updateData)
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your changes have been saved.",
      });

      // If this is a new user, redirect to create posit page
      if (isNewUser) {
        navigate("/dashboard/create");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Couldn’t update profile",
        description: "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = async (newEmail: string) => {
    if (!user || !newEmail || newEmail === user.email) return;

    setEmailChangeLoading(true);
    try {
      // Use Supabase's updateUser method to change email
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) throw error;

      toast({
        title: "Check your email",
        description: "We sent confirmation links to your current and new addresses.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Couldn’t update email",
        description: error.message || "Please try again.",
      });
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleNotificationChange = async (key: keyof NotificationSettings, value: boolean) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    
    setNotificationLoading(true);
    try {
      // In a real app, this would save to a database table
      // For now, using localStorage as a placeholder
      localStorage.setItem(`notifications_${user?.id}`, JSON.stringify(newSettings));
      
      toast({
        title: "Notifications updated",
        description: "Your preferences have been saved.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Couldn’t update notifications",
        description: "Please try again.",
      });
    } finally {
      setNotificationLoading(false);
    }
  };

  const copyPublicUrl = async () => {
    const handle = form.watch("handle") || currentHandle;
    if (!handle) return;

    const publicUrl = `${window.location.origin}/p/${handle}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setUrlCopied(true);
      toast({
        title: "URL copied",
        description: "Your public profile link is on the clipboard.",
      });
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Couldn’t copy URL",
        description: "Please copy the link manually.",
      });
    }
  };

  const saveHandle = async () => {
    if (!user || !isPremium) return;
    
    const newHandle = form.watch("handle");
    if (!newHandle) return;

    // Validate handle
    if (!validateHandle(newHandle)) {
      toast({
        variant: "destructive",
        title: "Invalid handle",
        description: "Use 3–30 characters: letters, numbers, and dashes (no consecutive dashes).",
      });
      return;
    }

    // Check if handle is the same as current
    if (newHandle === currentHandle) {
      toast({
        title: "No changes",
        description: "Your handle is already up to date.",
      });
      return;
    }

    // Check availability
    if (isHandleAvailable === false) {
      toast({
        variant: "destructive",
        title: "Handle not available",
        description: "That handle is taken. Please choose a different one.",
      });
      return;
    }

    setIsHandleSaving(true);
    try {
      const { error } = await supabase
        .from("users")
        .update({ handle: newHandle.toLowerCase() })
        .eq("id", user.id);

      if (error) throw error;

      setCurrentHandle(newHandle.toLowerCase());
      toast({
        title: "Handle updated",
        description: "Your public profile URL has been updated.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Couldn’t update handle",
        description: "Please try again.",
      });
    } finally {
      setIsHandleSaving(false);
    }
  };

  const redirectToPortal = async () => {
    setIsLoadingPortal(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No session found');
      }

      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { returnUrl: window.location.origin + '/edit-profile' }
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to access subscription portal. Please try again.",
      });
    } finally {
      setIsLoadingPortal(false);
    }
  };

  return (
    <DashboardLayout
      title={isNewUser ? "Complete Your Profile" : "Profile Settings"}
      description={isNewUser 
        ? "Set up your profile to get started with Posit" 
        : "Manage your account settings and preferences"
      }
      maxWidth="full"
    >

        <div className="grid gap-3 sm:gap-5 max-w-4xl mx-auto px-1 sm:px-0">
          {/* Personal Information */}
          <Card>
            <CardHeader className="p-4 pb-3">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
              <CardDescription>
                Your basic profile information
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm sm:text-base">First Name</FormLabel>
                          <FormControl>
                            <Input placeholder="John" {...field} className="h-10 text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm sm:text-base">Last Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Doe" {...field} className="h-10 text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="bio"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm sm:text-base">Profile Description</FormLabel>
                        <FormDescription className="text-xs sm:text-sm">
                          Tell others about yourself, your expertise, or what makes your content valuable.
                        </FormDescription>
                        <FormControl>
                          <Textarea 
                            placeholder="Share your story, expertise, or what visitors can expect from your content..."
                            className="min-h-[100px] resize-none text-sm"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" disabled={isLoading} className="h-10 w-full sm:w-auto text-sm">
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      isNewUser ? "Continue to Create Posit" : "Save Changes"
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Subscription & Plan Management */}
          <Card>
            <CardHeader className="p-4 pb-3">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Subscription & Plan
              </CardTitle>
              <CardDescription>
                Manage your subscription and billing settings
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3 sm:space-y-4">
                {/* Current Plan Status */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">Current Plan:</p>
                      {subscriptionType === 'posit_plus' ? (
                        <Badge variant="default" className="bg-primary">
                          Posit Plus
                        </Badge>
                      ) : subscriptionType === 'trial' ? (
                        <Badge variant="default" className="bg-primary text-primary-foreground">
                          Free Trial
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          Free Plan
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {subscriptionType === 'posit_plus' 
                        ? "Enjoy unlimited access to all premium features including custom profile URLs, advanced analytics, and priority support."
                        : subscriptionType === 'trial'
                        ? "You're currently enjoying all premium features during your free trial. Upgrade to Posit Plus to continue access after your trial ends."
                        : "Upgrade to Posit Plus for custom profile URLs, advanced analytics, and premium features."
                      }
                    </p>
                  </div>
                  <div className="flex gap-2 sm:flex-shrink-0">
                    {subscriptionType === 'posit_plus' ? (
                      <Button
                        variant="outline"
                        onClick={redirectToPortal}
                        disabled={isLoadingPortal}
                        className="flex items-center gap-2 w-full sm:w-auto h-10 text-sm"
                      >
                        {isLoadingPortal ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Settings className="h-4 w-4" />
                            Manage Plan
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => navigate('/dashboard/subscriptions')}
                        className="flex items-center gap-2 w-full sm:w-auto h-10 text-sm"
                      >
                        <CreditCard className="h-4 w-4" />
                        Upgrade to Plus
                      </Button>
                    )}
                  </div>
                </div>

                {/* Additional Management Options for Premium Users */}
                {isPremium && (
                  <div className="pt-3 border-t">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        variant="outline"
                        onClick={redirectToPortal}
                        disabled={isLoadingPortal}
                        className="flex items-center gap-2 h-10 text-sm w-full sm:w-auto"
                      >
                        {isLoadingPortal ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <ExternalLink className="h-4 w-4" />
                            Billing Portal
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Public Profile URL */}
          <Card>
            <CardHeader className="p-4 pb-3">
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Public Profile URL
                {isPremium && (
                  <Badge variant="default" className="bg-primary">Posit Plus</Badge>
                )}
              </CardTitle>
              <CardDescription>
                {isPremium 
                  ? "Customize your public profile URL to make it easy for others to find your Posits"
                  : "Your public profile URL where others can discover your Posits"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-4">
              {isPremium ? (
                <>
                  <Form {...form}>
                    <FormField
                      control={form.control}
                      name="handle"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm sm:text-base">Custom Handle</FormLabel>
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 min-w-0">
                                <Input
                                  placeholder="your-handle"
                                  {...field}
                                  className="h-10 text-sm"
                                  onChange={(e) => {
                                    field.onChange(e.target.value.toLowerCase());
                                  }}
                                />
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {isCheckingHandle && (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                )}
                                {isHandleAvailable === true && (
                                  <div className="flex items-center gap-1 text-green-600">
                                    <Check className="h-4 w-4" />
                                    <span className="text-sm">Available</span>
                                  </div>
                                )}
                                {isHandleAvailable === false && (
                                  <div className="flex items-center gap-1 text-red-600">
                                    <X className="h-4 w-4" />
                                    <span className="text-sm">Taken</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </FormControl>
                          <FormDescription className="text-xs sm:text-sm">
                            3-30 characters, letters, numbers, and dashes only. No consecutive dashes.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </Form>

                  {/* Save Handle Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={saveHandle}
                      disabled={
                        isHandleSaving || 
                        !form.watch("handle") || 
                        form.watch("handle") === currentHandle ||
                        isHandleAvailable === false ||
                        isCheckingHandle
                      }
                      className="flex items-center gap-2 h-10 text-sm w-full sm:w-auto"
                    >
                      {isHandleSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Handle"
                      )}
                    </Button>
                  </div>

                  {/* URL Preview */}
                  {(form.watch("handle") || currentHandle) && (
                      <div className="space-y-2">
                      <label className="text-sm font-medium">Your Public URL:</label>
                      <div className="flex flex-col gap-2 p-3 bg-muted rounded-md">
                        <span className="text-sm font-mono break-all leading-relaxed">
                          {window.location.origin}/p/{form.watch("handle") || currentHandle}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={copyPublicUrl}
                          className="flex items-center gap-2 w-full justify-center h-9 text-sm"
                        >
                          {urlCopied ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                          {urlCopied ? "Copied!" : "Copy"}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Share this URL to let others discover your public Posits
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-col gap-2 p-3 bg-muted/50 rounded-md">
                    <span className="text-sm font-mono text-muted-foreground break-all leading-relaxed">
                      {window.location.origin}/p/{currentHandle || "your-custom-handle"}
                    </span>
                    {currentHandle && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={copyPublicUrl}
                        className="flex items-center gap-2 w-full justify-center h-9 text-sm"
                      >
                        {urlCopied ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                        {urlCopied ? "Copied!" : "Copy"}
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {currentHandle 
                      ? "You have a public profile URL. Upgrade to Posit Plus to customize it."
                      : "Upgrade to Posit Plus to get a custom public profile URL."
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account Security */}
          <Card>
            <CardHeader className="p-4 pb-3">
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Account Security
              </CardTitle>
              <CardDescription>
                Manage your account security and login settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4 p-4">
              {/* Email Section */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1">
                  <p className="font-medium">Email Address</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  {isOAuthUser ? (
                    <Badge variant="outline" className="text-green-600 border-green-600 mt-1">
                      Google Account
                    </Badge>
                  ) : user?.email_confirmed_at ? (
                    <Badge variant="outline" className="text-green-600 border-green-600 mt-1">
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-yellow-600 border-yellow-600 mt-1">
                      Unverified
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2 sm:flex-shrink-0">
                  {user?.email && !isOAuthUser && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full sm:w-auto h-9 text-sm">
                          Change Email
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Change Email Address</AlertDialogTitle>
                          <AlertDialogDescription>
                            Changing your email will require verification. You'll receive confirmation emails at both your current and new email addresses.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="grid gap-4 py-4">
                          <Form {...form}>
                            <FormField
                              control={form.control}
                              name="email"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>New Email Address</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="email"
                                      placeholder="new@example.com"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </Form>
                        </div>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleEmailChange(form.getValues("email") || "")}
                            disabled={emailChangeLoading}
                          >
                            {emailChangeLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Updating...
                              </>
                            ) : (
                              "Update Email"
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>

              {/* Password Section - Only for non-OAuth users */}
              {!isOAuthUser && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-medium">Password</p>
                    <p className="text-sm text-muted-foreground">Update your account password</p>
                  </div>
                  <div className="sm:flex-shrink-0">
                    <ResetPasswordDialog />
                  </div>
                </div>
              )}

              {/* OAuth Info for Google users */}
              {isOAuthUser && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-blue-100 dark:bg-blue-900 rounded">
                      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-blue-900 dark:text-blue-100">
                        You're signed in with Google
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Your account is managed by Google. To change your email or password, please use your Google account settings.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
                Notification Preferences
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Choose what notifications you'd like to receive
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-4 py-4 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-base sm:text-lg">Email Notifications</p>
                  </div>
                  <div className="flex-shrink-0">
                    <Switch
                      checked={notificationSettings.emailNotifications}
                      onCheckedChange={(value) => handleNotificationChange('emailNotifications', value)}
                      disabled={notificationLoading}
                      className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted-foreground/20 w-11 h-6"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 py-4 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-base sm:text-lg">New Messages</p>
                  </div>
                  <div className="flex-shrink-0">
                    <Switch
                      checked={notificationSettings.newMessages}
                      onCheckedChange={(value) => handleNotificationChange('newMessages', value)}
                      disabled={notificationLoading}
                      className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted-foreground/20 w-11 h-6"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 py-4 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-base sm:text-lg">Product Updates</p>
                  </div>
                  <div className="flex-shrink-0">
                    <Switch
                      checked={notificationSettings.productUpdates}
                      onCheckedChange={(value) => handleNotificationChange('productUpdates', value)}
                      disabled={notificationLoading}
                      className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted-foreground/20 w-11 h-6"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 py-4 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-base sm:text-lg">Marketing Communications</p>
                  </div>
                  <div className="flex-shrink-0">
                    <Switch
                      checked={notificationSettings.marketingEmails}
                      onCheckedChange={(value) => handleNotificationChange('marketingEmails', value)}
                      disabled={notificationLoading}
                      className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted-foreground/20 w-11 h-6"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Setup */}
          <StripeConnectOnboarding />

          {/* Log out */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LogOut className="h-5 w-5" />
                Log out
              </CardTitle>
              <CardDescription>
                Sign out of your account on this device
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" onClick={signOut} className="h-11">
                <LogOut className="h-4 w-4 mr-2" />
                Log out
              </Button>
            </CardContent>
          </Card>
        </div>
    </DashboardLayout>
  );
};

export default EditProfile;
